import { useState } from 'react';
import { unescapeHtmlEntities } from '@/utils/stringUtils';
import PlanDetailsDialog from '@/app/components/PlanDetailsDialog';
import { trpc } from '@/app/api/trpc/client';
import { cn } from '@/utils/uiUtils';
import type { StructuredCard } from './types';

type PlanSelectionCardProps = {
  card: StructuredCard & { content: string; title?: string };
};

type PlanPreviewData = {
  id: string;
  name: string;
  goal?: string;
  length_weeks?: number;
  description?: string;
};

export const PlanSelectionCard = ({ card }: PlanSelectionCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);

  let planData: PlanPreviewData = {
    id: '',
    name: '',
    goal: '',
    length_weeks: 0,
    description: '',
  };
  try {
    const parsed = JSON.parse(card.content);
    if (parsed && typeof parsed === 'object') {
      planData = {
        id: String((parsed as Record<string, unknown>).id ?? planData.id),
        name:
          String((parsed as Record<string, unknown>).name ?? planData.name) ||
          card.title ||
          '',
        goal: String(
          (parsed as Record<string, unknown>).goal ?? planData.goal ?? ''
        ),
        length_weeks:
          Number(
            (parsed as Record<string, unknown>).length_weeks ??
              (parsed as Record<string, unknown>).lengthWeeks ??
              planData.length_weeks
          ) || 0,
        description: String(
          (parsed as Record<string, unknown>).description ??
            planData.description ??
            ''
        ),
      };
    }
  } catch {
    planData = {
      id: card.content,
      name: card.title || '',
      goal: '',
      length_weeks: 0,
      description: '',
    };
  }

  // Fetch full plan details for the dialog
  const { data: fullPlan } = trpc.workouts.getPlanById.useQuery(
    { id: planData.id },
    { enabled: isDialogOpen }
  );

  const handlePlanSelected = () => {
    if (hasSelected) return;
    setHasSelected(true);
    // After DB persistence is done in Dialog, we inform the coach
    const input = document.getElementById('chat-input');
    if (input instanceof HTMLInputElement) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      nativeInputValueSetter?.call(
        input,
        `I have selected the plan: ${planData.name} (ID: ${planData.id}). Please proceed with my onboarding.`
      );
      input.dispatchEvent(new Event('input', { bubbles: true }));
      setTimeout(
        () =>
          input.form?.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
          ),
        10
      );
    }
  };

  return (
    <>
      <button
        disabled={hasSelected}
        className={cn(
          'w-64 text-left p-3 border rounded-xl transition-all shadow-sm group flex flex-col justify-between',
          hasSelected
            ? 'bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-60 cursor-not-allowed'
            : 'bg-white dark:bg-slate-800 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 border-slate-200 dark:border-slate-700'
        )}
        onClick={() => setIsDialogOpen(true)}
      >
        <div>
          <div
            className={cn(
              'font-bold transition-colors line-clamp-2',
              hasSelected
                ? 'text-slate-500 dark:text-slate-400'
                : 'text-slate-900 dark:text-slate-100 group-hover:text-yellow-600 dark:group-hover:text-yellow-400'
            )}
          >
            {unescapeHtmlEntities(planData.name)}
          </div>
          {planData.goal ||
          (typeof planData.length_weeks === 'number' &&
            planData.length_weeks > 0) ? (
            <div className='flex gap-2 mt-2 text-xs font-medium text-slate-500 dark:text-slate-400'>
              {planData.goal && (
                <span className='bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded'>
                  {planData.goal}
                </span>
              )}
              {typeof planData.length_weeks === 'number' &&
                planData.length_weeks > 0 && (
                  <span className='bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded'>
                    {planData.length_weeks} weeks
                  </span>
                )}
            </div>
          ) : null}
          {planData.description && (
            <div className='text-xs text-slate-500 dark:text-slate-400 mt-2 line-clamp-3'>
              {unescapeHtmlEntities(planData.description)}
            </div>
          )}
        </div>
      </button>

      <PlanDetailsDialog
        plan={fullPlan ?? null}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onPlanSelected={handlePlanSelected}
      />
    </>
  );
};
